import React, { useEffect } from 'react';
import { Switch, Route, Redirect, Link, withRouter, useLocation, HashRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Home from './components/Home';
import MyGallery from './components/UserGallery/MyGallery';
import ColoringPage from './components/ImageGallery/ColoringPage';
import MyFeaturedImage from './components/UserGallery/MyFeaturedImage';
import MyShoppingCart from './components/UserGallery/MyShoppingCart';
import MyOrders from './components/UserGallery/MyOrders';
import ImageGallery from './components/ImageGallery/ImageGallery';
import AdminImageEditor from './components/AdminImageEditor';

// 👑 EXACT DIRECTORY TREE ALIGNMENT MAP 👑
import AllUserGalleries from './components/UserGallery/AllUsersGallery';
import AllOrders from './components/UserGallery/AllOrders';
import UsersSearch from './components/UserGallery/AllUserSearch';
import AdminUsersEditor from './components/AdminUserEditor'; 

import './App.css';

const API_BASE_URL = "https://test-api.stainedglassusa.com";

const App = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { token } = useSelector(state => state.auth);

    const wpUser = window.wp_user || null; 
    const isAdmin = wpUser?.is_admin || false;
    const isLoggedIn = !!wpUser;

    useEffect(() => {
        if (isLoggedIn && wpUser && (!token || token === 'wordpress_session')) {
            
            const fallbackUsername = wpUser.email ? wpUser.email.split('@')[0] : `wpuser_${wpUser.id}`;
            const appUsername = wpUser.display_name ? wpUser.display_name.replace(/\s+/g, '').toLowerCase() : fallbackUsername;
            const appEmail = wpUser.email || `${appUsername}@stainedglasswindows.com`;
            
            // Strong complex baseline key
            const systemSecurePassword = `Wp_Secure_Studio_Pass_Asset_True_#_${wpUser.id}_!770`;

            const loginPayload = {
                username: appUsername,
                password: systemSecurePassword
            };

            console.log(`[App Initializer] Performing database handshake for: "${appUsername}"`);

            // STEP 1: Try Logging in
            fetch(`${API_BASE_URL}/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(loginPayload)
            })
            .then(r => {
                if (r.status === 401) {
                    return { error: 'needs_registration' };
                }
                return r.json();
            })
            .then(loginResponse => {
                if (loginResponse.token && loginResponse.user) {
                    console.log("[App Initializer] Authenticated successfully. User ID:", loginResponse.user.id);
                    localStorage.setItem('token', loginResponse.token);
                    dispatch({
                        type: 'SET_USER',
                        payload: loginResponse
                    });
                } 
                // STEP 2: Force complete raw-parameter fallback mapping to break past strict filters
                else {
                    console.log("[App Initializer] Account absent. Running dual-parameter force save...");
                    
                    const universalPayload = {
                        username: appUsername,
                        email: appEmail,
                        password: systemSecurePassword,
                        password_confirmation: systemSecurePassword,
                        password_digest: systemSecurePassword,
                        admin: isAdmin,
                        wp_id: wpUser.id,
                        user: {
                            username: appUsername,
                            email: appEmail,
                            password: systemSecurePassword,
                            password_confirmation: systemSecurePassword,
                            password_digest: systemSecurePassword,
                            admin: isAdmin,
                            wp_id: wpUser.id
                        }
                    };

                    fetch(`${API_BASE_URL}/users`, {
                        method: 'POST',
                        headers: { 
                            'Content-Type': 'application/json',
                            'Accept': 'application/json'
                        },
                        body: JSON.stringify(universalPayload)
                    })
                    .then(res => res.json())
                    .then(signupResponse => {
                        if (signupResponse.token && signupResponse.user) {
                            console.log("[App Initializer] FORCE SAVE NATIVE SUCCESS! Ruby User ID:", signupResponse.user.id);
                            localStorage.setItem('token', signupResponse.token);
                            dispatch({
                                type: 'SET_USER',
                                payload: signupResponse
                            });
                        } else {
                            console.error("[App Initializer] Rails structural parameter rejection:", signupResponse.errors || signupResponse);
                        }
                    });
                }
            })
            .catch(err => {
                console.error("[App Initializer] Net interface crash:", err);
            });
        }
    }, [dispatch, isLoggedIn, wpUser, token, isAdmin]);

    return (
        <div className="App embedded-in-wordpress">
            {isLoggedIn && (
                <nav className="tool-internal-navbar">
                    {/* --- Standard Customer Core Links --- */}
                    <Link to="/" className={location.pathname === '/' ? 'active' : ''}>🏠 Click'N Color™</Link>
                    <Link to="/images" className={location.pathname === '/images' ? 'active' : ''}>🎨 Choose Pattern</Link>
                    <Link to="/mygallery" className={location.pathname === '/mygallery' ? 'active' : ''}>🖼️ My Creations</Link>
                    <Link to="/quotes" className={location.pathname === '/quotes' ? 'active' : ''}>📋 My Quotes</Link>

                    {/* --- 👑 Administrative Core Links --- */}
                    {isAdmin && (
                        <>
                            <span className="navbar-divider-pipe" style={{ margin: '0 10px', color: '#666' }}>|</span>
                            <Link to="/all-gallery" className={location.pathname === '/all-gallery' ? 'active' : ''}>📂 All User Galleries</Link>
                            <Link to="/users-search" className={location.pathname === '/users-search' ? 'active' : ''}>🔍 Search Users</Link>
                            <Link to="/admin/images/edit" className={location.pathname === '/admin/images/edit' ? 'active' : ''}>🛠️ Edit Designs</Link>
                            <Link to="/all-orders" className={location.pathname === '/all-orders' ? 'active' : ''}>📦 All Quotes Admin</Link>
                        </>
                    )}
                </nav>
            )}
            
            <div className="main-content">
                <Switch>
                    <Route path="/" exact component={Home} />
                    <Route path="/images" exact component={ImageGallery} />
                    <Route path="/coloringpage/:id" component={ColoringPage} />
                    <Route path="/mygallery" component={MyGallery} />
                    <Route path="/featuredimage/:id" component={MyFeaturedImage} />
                    <Route path="/cart" component={MyShoppingCart} />
                    <Route path="/quotes" component={MyOrders} />

                    {/* --- Protected Administrative Suite --- */}
                    {isAdmin && (
                        <>
                            <Route path="/all-gallery" component={AllUserGalleries} />
                            <Route path="/admin/images/edit" component={AdminImageEditor} />
                            <Route path="/all-orders" component={AllOrders} />
                            <Route path="/users-search" component={UsersSearch} />
                            <Route path="/admin/users/edit" component={AdminUsersEditor} />
                        </>
                    )}
                    <Redirect to="/" />
                </Switch>
            </div>
        </div>
    );
}

const RoutedApp = withRouter(App);

const WordPressApp = () => {
    return (
        <HashRouter>
            <RoutedApp />
        </HashRouter>
    );
};

export default WordPressApp;